import { styled, css } from 'styled-components'

import {
  Table as BaseTable,
  TableBody as BaseTableBody,
  TableCaption as BaseTableCaption,
  TableCell as BaseTableCell,
  TableHead as BaseTableHead,
  TableHeader as BaseTableHeader,
  TableRow as BaseTableRow,
} from 'core/components/ui/table'
import { onSurface, onSurfaceMuted, onSurfaceVariant, outline, surfaceBright, surfaceDim, surface } from 'core/styles'

export const TableWrapper = styled.div<{ $bordered?: boolean }>`
  box-sizing: border-box;
  position: relative;
  background: ${surfaceBright};
  width: 100%;
  max-width: 100%;
  min-height: 80px;
  overflow: auto;
  ${(p) =>
    p.$bordered &&
    css`
      border: 1px solid ${outline};
      border-radius: 8px;
    `}
`

export const TableViewport = styled.div<{ $maxWidth?: string }>`
  width: 100%;
  max-width: ${(p) => p.$maxWidth ?? '100%'};
  overflow-x: auto;
`

export const StyledTable = styled(BaseTable)`
  background: ${surfaceBright};
  width: 100%;
  table-layout: auto;
  border-collapse: collapse;
  color: ${onSurface};
`

export const StyledTableHeader = styled(BaseTableHeader)`
  background: ${surfaceDim};
`

export const StyledTableBody = styled(BaseTableBody)``

export const StyledTableRow = styled(BaseTableRow)<{
  $highlight?: boolean
  $summary?: boolean
}>`
  border-bottom: 1px solid ${outline};

  &:last-child {
    border-bottom: none;
  }

  ${(p) =>
    p.$summary &&
    css`
      font-weight: 600;
    `}

  ${(p) =>
    p.$highlight &&
    css`
      &:hover {
        background: ${surface};
        cursor: pointer;
      }
    `}
`

export const StyledTableHead = styled(BaseTableHead)<{
  $sortable?: boolean
  $align?: 'left' | 'center' | 'right'
  $width?: string | number
  $pinned?: 'left' | 'right' | false
  $left?: number
  $isLastPinned?: boolean
}>`
  padding: 8px;
  text-align: ${(p) => p.$align || 'left'};
  line-height: 24px;
  letter-spacing: 0.5px;
  white-space: nowrap;
  color: ${onSurface};
  font-size: 14px;
  font-weight: 600;
  ${(p) => p.$width && `width: ${typeof p.$width === 'number' ? `${p.$width}px` : p.$width};`}

  ${(p) =>
    p.$sortable &&
    css`
      cursor: pointer;
      user-select: none;

      &:hover {
        background: ${outline};
      }
    `}

  ${(p) =>
    p.$pinned &&
    css`
      position: sticky;
      ${p.$pinned === 'left' ? `left: ${p.$left ?? 0}px;` : `right: ${p.$left ?? 0}px;`}
      z-index: 2;
      background: ${surfaceDim};
    `}

  ${(p) =>
    p.$isLastPinned &&
    css`
      &::after {
        position: absolute;
        top: 0;
        ${p.$pinned === 'left' ? 'right: 0;' : 'left: 0;'}
        background-color: ${outline};
        width: 2px;
        height: 100%;
        content: '';
      }
    `}
`

export const StyledTableCell = styled(BaseTableCell)<{
  $align?: 'left' | 'center' | 'right'
  $width?: string | number
  $summary?: boolean
  $pinned?: 'left' | 'right' | false
  $left?: number
  $isLastPinned?: boolean
}>`
  border-bottom: 1px solid ${outline};
  padding: ${(p) => (p.$summary ? '12px 8px' : '8px')};
  vertical-align: middle;
  text-align: ${(p) => p.$align || 'left'};
  line-height: 24px;
  white-space: nowrap;
  font-size: 14px;
  ${(p) => p.$width && `width: ${typeof p.$width === 'number' ? `${p.$width}px` : p.$width};`}
  ${StyledTableRow}:last-child & {
    border-bottom: none;
  }

  ${(p) =>
    p.$pinned &&
    css`
      position: sticky;
      ${p.$pinned === 'left' ? `left: ${p.$left ?? 0}px;` : `right: ${p.$left ?? 0}px;`}
      z-index: 1;
      background-color: ${surfaceBright};
    `}

  ${(p) =>
    p.$isLastPinned &&
    css`
      &::after {
        position: absolute;
        top: 0;
        ${p.$pinned === 'left' ? 'right: 0;' : 'left: 0;'}
        background-color: ${outline};
        width: 2px;
        height: 100%;
        content: '';
      }
    `}
`

export const StyledTableCaption = styled(BaseTableCaption)`
  margin-top: 8px;
  caption-side: bottom;
  color: ${onSurfaceVariant};
  font-size: 12px;
`

/** Secondary/muted text inside table cells (e.g. IDs, sub-labels). Use plain text for primary cell content. */
export const CellSubText = styled.div`
  color: ${onSurfaceMuted};
  font-size: 14px;
`
